import { collection, doc, DocumentData, onSnapshot, query, Unsubscribe } from 'firebase/firestore';
import { db } from '../../../shared/utils/firebase';
import { whereLocation } from '../../auth/managedLocations';
import {
  AwaitingClient,
  OverdueAge,
  OverdueClient,
  StaffPerformanceScope,
  StaffSuccessUpdates,
  SuccessUpdateCounts,
  SuccessUpdatesMeta,
} from '../types';
import { asDate } from './staffPerformanceService';

export const SUCCESS_UPDATES_COLLECTION = 'staff_success_updates';
export const SUCCESS_UPDATES_META_COLLECTION = 'staff_success_updates_meta';
const META_DOC_ID = 'latest';
/** Document id of the admin-only bucket for clients with no usable lead coach. */
export const UNASSIGNED_DOC_ID = '__unassigned';
const LOG_PREFIX = '[staff-performance]';

const objectOr = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
const numberOr0 = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);
const stringOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const arrayOrEmpty = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

function mapCounts(value: unknown): SuccessUpdateCounts {
  const counts = objectOr(value);
  return {
    active: numberOr0(counts.active),
    overdue: numberOr0(counts.overdue),
    awaiting_first: numberOr0(counts.awaiting_first),
    due_soon: numberOr0(counts.due_soon),
    on_track: numberOr0(counts.on_track),
    not_yet_due: numberOr0(counts.not_yet_due),
  };
}

function mapOverdueAge(value: unknown): OverdueAge {
  const age = objectOr(value);
  return {
    weeks_6_8: numberOr0(age.weeks_6_8),
    weeks_8_12: numberOr0(age.weeks_8_12),
    weeks_12_plus: numberOr0(age.weeks_12_plus),
  };
}

/** `staff_success_updates/{id}`; `id` is the document id (the staff id, or `UNASSIGNED_DOC_ID`). */
export function mapSuccessUpdatesDoc(id: string, data: DocumentData): StaffSuccessUpdates {
  return {
    id,
    staff_id: typeof data.staff_id === 'string' ? data.staff_id : id,
    display_name: stringOrNull(data.display_name),
    home_location: stringOrNull(data.home_location),
    counts: mapCounts(data.counts),
    overdue_age: mapOverdueAge(data.overdue_age),
    longest_overdue_days: typeof data.longest_overdue_days === 'number' ? data.longest_overdue_days : null,
    overdue_clients: arrayOrEmpty<OverdueClient>(data.overdue_clients),
    awaiting_clients: arrayOrEmpty<AwaitingClient>(data.awaiting_clients),
    run_id: typeof data.run_id === 'string' ? data.run_id : '',
    computed_at: asDate(data.computed_at),
  };
}

/** `staff_success_updates_meta/latest`. */
export function mapSuccessUpdatesMeta(data: DocumentData): SuccessUpdatesMeta {
  return {
    status: data.status === 'error' ? 'error' : 'ok',
    computed_at: asDate(data.computed_at),
    last_success_at: asDate(data.last_success_at),
    totals: data.totals && typeof data.totals === 'object' ? mapCounts(data.totals) : null,
    staff: numberOr0(data.staff),
    unassigned_active: numberOr0(data.unassigned_active),
    error: stringOrNull(data.error),
  };
}

/** Realtime, role-scoped rollups, scoped the same way as `subscribeToStaffPerformance`. */
export function subscribeToSuccessUpdates(
  scope: StaffPerformanceScope,
  onData: (docs: StaffSuccessUpdates[]) => void,
  onError: (error: Error) => void
): Unsubscribe {
  if (scope.kind === 'none') {
    onError(new Error(scope.reason));
    return () => undefined;
  }

  const handleError = (error: Error) => {
    console.error(`${LOG_PREFIX} success updates subscription failed`, error);
    onError(error);
  };

  if (scope.kind === 'self') {
    return onSnapshot(
      doc(db, SUCCESS_UPDATES_COLLECTION, scope.staffId),
      (snapshot) => onData(snapshot.exists() ? [mapSuccessUpdatesDoc(snapshot.id, snapshot.data() ?? {})] : []),
      handleError
    );
  }

  const base = collection(db, SUCCESS_UPDATES_COLLECTION);
  const target = scope.kind === 'location' ? query(base, whereLocation('home_location', scope.locations)) : base;
  return onSnapshot(target, (snapshot) => onData(snapshot.docs.map((d) => mapSuccessUpdatesDoc(d.id, d.data()))), handleError);
}

/** `staff_success_updates_meta/latest` for the header chip; `null` until the first run has written it. */
export function subscribeToSuccessUpdatesMeta(
  onData: (meta: SuccessUpdatesMeta | null) => void,
  onError: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, SUCCESS_UPDATES_META_COLLECTION, META_DOC_ID),
    (snapshot) => onData(snapshot.exists() ? mapSuccessUpdatesMeta(snapshot.data() ?? {}) : null),
    (error) => {
      console.error(`${LOG_PREFIX} success updates meta subscription failed`, error);
      onError(error);
    }
  );
}
